<script setup lang="ts">
import { QuestionType } from "@/models/enum/questionType";
import AddressQuestion from "@/components/questionnaire/questions/AddressQuestion.vue";
import BooleanQuestion from "@/components/questionnaire/questions/BooleanQuestion.vue";
import BooleanWithDetailsQuestion from "@/components/questionnaire/questions/BooleanWithDetailsQuestion.vue";
import CertificatesQuestion from "@/components/questionnaire/questions/CertificatesQuestion.vue";
import CheckboxQuestion from "@/components/questionnaire/questions/CheckboxQuestion.vue";
import ConsentQuestion from "@/components/questionnaire/questions/ConsentQuestion.vue";
import DateQuestion from "@/components/questionnaire/questions/DateQuestion.vue";
import InputQuestion from "@/components/questionnaire/questions/InputQuestion.vue";
import ListOfContactPersonsQuestion from "@/components/questionnaire/questions/ListOfContactPersonsQuestion.vue";
import SelectQuestion from "@/components/questionnaire/questions/SelectQuestion.vue";
import TextAreaInputQuestion from "@/components/questionnaire/questions/TextAreaInputQuestion.vue";
import ListOfCheckboxQuestion from "@/components/questionnaire/questions/ListOfCheckboxQuestion.vue";
import QuestionLeadText from "@/components/questionnaire/questions/QuestionLeadText.vue";
import type { QuestionModelDto } from "@/models/dto/questionModelDto";
import BooleanWithMultiSelectQuestion from "@/components/questionnaire/questions/BooleanWithMultiSelectQuestion.vue";
import KeyPersonnelQuestion from "./questions/KeyPersonnelQuestion.vue";
import OwnershipQuestion from "./questions/OwnershipQuestion.vue";
import PhoneQuestion from "./questions/PhoneQuestion.vue";
import MultiselectQuestion from "@/components/questionnaire/questions/MultiselectQuestion.vue";
import { computed } from "vue";
import OwnershipIndividualQuestion from "@/components/questionnaire/questions/OwnershipIndividualQuestion.vue";
import OwnershipCompanyQuestion from "@/components/questionnaire/questions/OwnershipCompanyQuestion.vue";
import PersonWithPepAndSanctionsQuestion from "@/components/questionnaire/questions/PersonWithPepAndSanctionsQuestion.vue";
import PersonsWithPepAndSanctionsQuestion from "@/components/questionnaire/questions/PersonsWithPepAndSanctionsQuestion.vue";
import PersonsNameAndPositionQuestion from "@/components/questionnaire/questions/PersonsNameAndPositionQuestion.vue";
import BooleanWithDetailsOnNoQuestion from "@/components/questionnaire/questions/BooleanWithDetailsOnNoQuestion.vue";
import CertificatesQuestionV2 from "@/components/questionnaire/questions/CertificatesQuestionV2.vue";

const props = withDefaults(
  defineProps<{
    isReportMode?: boolean;
    questionModel: QuestionModelDto;
    answer?: any;
    isReadOnlyMode?: boolean;
    isConditionFulfilled: boolean;
  }>(),
  {
    isReportMode: false,
    isReadOnlyMode: false,
    isConditionFulfilled: true,
  }
);

const emit = defineEmits<{
  (e: "qEvent", newAnswer: object): void;
  (e: "isValid", isValid: boolean, invalidField: string): void;
}>();

const questionType = computed(() => props.questionModel.question.type);

function emitNewAnswer(value: any): void {
  emit("qEvent", value as unknown as object);
}

function emitIsValid(value: boolean, invalidField: string): void {
  emit("isValid", value, invalidField);
}

const questions = {
  [QuestionType.LeadText]: QuestionLeadText,
  [QuestionType.Address]: AddressQuestion,
  [QuestionType.Boolean]: BooleanQuestion,
  [QuestionType.BooleanWithDetails]: BooleanWithDetailsQuestion,
  [QuestionType.BooleanWithDetailsOnNo]: BooleanWithDetailsOnNoQuestion,
  [QuestionType.BooleanWithMultiSelect]: BooleanWithMultiSelectQuestion,
  [QuestionType.BooleanWithNotRelevant]: BooleanQuestion,
  [QuestionType.Certificates]: CertificatesQuestion,
  [QuestionType.CertificatesV2]: CertificatesQuestionV2,
  [QuestionType.Checkbox]: CheckboxQuestion,
  [QuestionType.Consent]: ConsentQuestion,
  [QuestionType.Date]: DateQuestion,
  [QuestionType.Text]: InputQuestion,
  [QuestionType.ListOfCheckboxes]: ListOfCheckboxQuestion,
  [QuestionType.ListOfContactPersons]: ListOfContactPersonsQuestion,
  [QuestionType.Select]: SelectQuestion,
  [QuestionType.TextArea]: TextAreaInputQuestion,
  [QuestionType.KeyPersonnel]: KeyPersonnelQuestion,
  [QuestionType.BeneficialOwners]: OwnershipQuestion,
  [QuestionType.Phone]: PhoneQuestion,
  [QuestionType.Multiselect]: MultiselectQuestion,
  [QuestionType.OwnershipPubliclyListedTypes]: BooleanQuestion,
  [QuestionType.OwnershipIndividual]: OwnershipIndividualQuestion,
  [QuestionType.OwnershipCompany]: OwnershipCompanyQuestion,
  [QuestionType.PersonWithPepAndSanctions]: PersonWithPepAndSanctionsQuestion,
  [QuestionType.PersonsWithPepAndSanctions]: PersonsWithPepAndSanctionsQuestion,
  [QuestionType.PersonsNameAndPosition]: PersonsNameAndPositionQuestion,
};

function getQuestion(): any {
  return questions[questionType.value];
}
</script>

<template>
  <component
    v-if="isReportMode"
    :is="getQuestion()"
    :question-model="questionModel"
    :answer="answer"
    :is-report-mode="isReportMode"
  />

  <component
    v-else-if="isReadOnlyMode"
    :is="getQuestion()"
    :question-model="questionModel"
    :answer="answer"
    :is-read-only-mode="isReadOnlyMode"
  />

  <component
    v-else-if="isConditionFulfilled"
    :is="getQuestion()"
    :question-model="questionModel"
    :answer="answer"
    :is-report-mode="isReportMode"
    @q-event="emitNewAnswer"
    @is-valid="emitIsValid"
  />
</template>
